import { useCallback, useEffect, useState } from "react";
import { fetchGoogleRoomDetails, updateCraneGuageSessionMeetLink } from "./api";
import { readMeetingUri } from "./craneSessions";

function getOpenableMeetUrl(value) {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function useGoogleMeet({ podSession, updatePodSession }) {
  const [meetLoading, setMeetLoading] = useState(false);
  const [meetLinkLoading, setMeetLinkLoading] = useState(false);
  const [localMeetLink, setLocalMeetLink] = useState("");

  useEffect(() => {
    setLocalMeetLink(podSession?.googleMeetLink ?? "");
  }, [podSession?.id, podSession?.googleMeetLink]);

  const syncLinkedCraneMeetLink = useCallback(
    async (meetLink) => {
      if (podSession?.organizationId !== "crane") return;
      const integrationID = String(podSession?.integrationID ?? "").trim();
      if (!integrationID || !meetLink) return;

      try {
        await updateCraneGuageSessionMeetLink(integrationID, meetLink);
      } catch (error) {
        console.error("Failed to save Crane google_meet_id", error);
      }
    },
    [podSession?.integrationID, podSession?.organizationId],
  );

  const handleGenerateGoogleMeetLink = useCallback(async () => {
    setMeetLoading(true);

    try {
      const parsed = await fetchGoogleRoomDetails("createRoom", {
        clientId: podSession.organizationId,
      });
      const meetLink =
        parsed.statusCode === 200 ? readMeetingUri(parsed.body) : "";

      if (meetLink) {
        await updatePodSession({ googleMeetLink: meetLink });
        setLocalMeetLink(meetLink);
        await syncLinkedCraneMeetLink(meetLink);
      } else {
        console.error("Failed to generate Google Meet link", parsed);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setMeetLoading(false);
    }
  }, [
    podSession?.organizationId,
    syncLinkedCraneMeetLink,
    updatePodSession,
  ]);

  const handleSaveMeetLink = useCallback(async () => {
    setMeetLinkLoading(true);
    const urlToOpen = getOpenableMeetUrl(localMeetLink);
    const meetTab = urlToOpen ? window.open("", "_blank") : null;

    try {
      await updatePodSession({ googleMeetLink: localMeetLink });
      await syncLinkedCraneMeetLink(localMeetLink);
      if (!urlToOpen) return;
      if (meetTab) {
        meetTab.location.href = urlToOpen;
      } else {
        window.open(urlToOpen, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error(error);
      meetTab?.close();
    } finally {
      setMeetLinkLoading(false);
    }
  }, [localMeetLink, syncLinkedCraneMeetLink, updatePodSession]);

  return {
    localMeetLink,
    setLocalMeetLink,
    meetLoading,
    meetLinkLoading,
    handleGenerateGoogleMeetLink,
    handleSaveMeetLink,
  };
}
